import { useEffect, useRef } from "react";
import axios, { AxiosError } from "axios";
import { PaymentRoutes, isErrorPage } from "../../Contracts/Routes";
import { useBannerStore } from "../../Store/Banner";
import { BannerType } from "../../Contracts/Banner";
import { PaymentErrorResponse } from "../../Contracts/Error";

interface InterceptorCleanup {
  cleanup: () => void;
}

export const isPaymentsIntegration409 = (
  error: unknown
): error is AxiosError<PaymentErrorResponse> => {
  if (!axios.isAxiosError<PaymentErrorResponse>(error)) return false;
  if (error.response?.status !== 409) return false;
  return !!error.response.data?.child_exceptions?.some(
    c => c.exception_type === "payments_integration_exception"
  );
};

export const setupAxiosInterceptors = (
  addBanner: (banner: { type: BannerType; message: string }) => void
): InterceptorCleanup => {
  const responseInterceptor = axios.interceptors.response.use(
    response => response,
    async error => {
      const currentPath = window.location.pathname;
      const isAlreadyOnErrorPage = isErrorPage(currentPath);
      
      if (error.response?.status === 500 && !isAlreadyOnErrorPage) {
        window.location.href = PaymentRoutes.INTERNAL_SERVER_ERROR;
      }
      
      if (error.response?.status === 401 && !isAlreadyOnErrorPage) {
        const hasPaymentCancelledException = error.response?.data?.child_exceptions?.some(
          (exception: any) => exception.exception_type === "payment_account_cancelled_exception"
        );

        if (hasPaymentCancelledException) {
          window.location.href = PaymentRoutes.ACCOUNT_CANCELLED;
        } else {
          const isClientPortalRoute = currentPath.startsWith("/client-portal");
          const redirect = isClientPortalRoute
            ? `${PaymentRoutes.SESSION_ENDED}?fromClientPortal=true`
            : PaymentRoutes.SESSION_ENDED;
          window.location.href = redirect;
        }
      }
      
      if (error.response?.status === 400 || error.response?.status === 404 || isPaymentsIntegration409(error)) {
        const url = error.config?.url || '';
        const shouldShowBanner =
          !url.includes("/payments/views/") &&
          !url.includes("/invite/") &&
          !url.includes("/accept/") &&
          !url.includes("/checkout/otp/");

        if (shouldShowBanner) {
          addBanner({
            type: BannerType.Error,
            message: error.response.data?.description || "An error occurred"
          });
        }
      }
      
      throw error;
    }
  );

  return {
    cleanup: () => {
      axios.interceptors.response.eject(responseInterceptor);
    }
  };
};

export const useAxiosInterceptor = (): void => {
  const { addBanner } = useBannerStore();
  const interceptorCleanup = useRef<InterceptorCleanup | null>(null);

  useEffect(() => {
    interceptorCleanup.current = setupAxiosInterceptors(addBanner);

    return () => {
      if (interceptorCleanup.current) {
        interceptorCleanup.current.cleanup();
      }
    };
  }, [addBanner]);
};